import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchContractById,
  updateContract,
  processApproval,
  addSignature,
  setShowSignatureModal,
  setShowApprovalModal,
  clearCurrentContract,
  clearError,
} from '../../store/slices/contractsSlice';
import type { RootState, AppDispatch } from '../../store';
import type { Contract, ApprovalAction, DigitalSignatureData } from '../../types/contracts';
import { CONTRACT_STATUS_COLORS } from '../../types/contracts';
import contractService from '../../services/contractService';
import ContractMilestones from './ContractMilestones';
import ContractComments from './ContractComments';
import ContractVersions from './ContractVersions';
import ContractApprovals from './ContractApprovals';
import DigitalSignatureModal from './DigitalSignatureModal';
import ApprovalModal from './ApprovalModal';
import DatePicker from '../forms/DatePicker';

const ContractDetails: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { projectId, contractId } = useParams<{ projectId: string; contractId: string }>();

  const {
    currentContract: contract,
    loading,
    error,
    showSignatureModal,
    showApprovalModal,
  } = useSelector((state: RootState) => state.contracts);

  const [activeTab, setActiveTab] = useState<
    'overview' | 'milestones' | 'versions' | 'approvals' | 'comments'
  >('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Contract> | null>(null);

  // Load contract on mount
  useEffect(() => {
    if (contractId) {
      dispatch(fetchContractById(contractId));
    }

    return () => {
      dispatch(clearCurrentContract());
      dispatch(clearError());
    };
  }, [dispatch, contractId]);

  // Initialize edit form when contract loads
  useEffect(() => {
    if (contract && !editFormData) {
      setEditFormData({
        title: contract.title,
        description: contract.description,
        amount: contract.amount,
        currency: contract.currency,
        startDate: contract.startDate,
        endDate: contract.endDate,
        scope: contract.scope,
        tags: contract.tags,
      });
    }
  }, [contract, editFormData]);

  // Handle contract update
  const handleUpdateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contract || !editFormData) return;

    try {
      await dispatch(
        updateContract({
          contractId: contract.id,
          data: editFormData,
        })
      ).unwrap();
      setIsEditing(false);
      setEditFormData(null);
    } catch (error) {
      console.error('Failed to update contract:', error);
    }
  };

  // Handle approval
  const handleApproval = async (approval: ApprovalAction) => {
    if (!contract) return;

    try {
      await dispatch(
        processApproval({
          contractId: contract.id,
          action: approval,
        })
      ).unwrap();
    } catch (error) {
      console.error('Failed to process approval:', error);
    }
  };

  // Handle digital signature
  const handleSignature = async (signatureData: DigitalSignatureData) => {
    if (!contract) return;

    try {
      await dispatch(
        addSignature({
          contractId: contract.id,
          signatureData,
        })
      ).unwrap();
    } catch (error) {
      console.error('Failed to add signature:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <svg
            className="w-12 h-12 text-red-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-medium text-red-900 mb-2">Contract not found</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/projects/${projectId}/contracts`)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        <div className="text-center py-12">
          <p className="text-gray-500">Contract not found</p>
        </div>
      </div>
    );
  }

  const statusColor = CONTRACT_STATUS_COLORS[contract.status];
  const isExpired = contractService.isContractExpired(contract);
  const daysUntilExpiration = contractService.getDaysUntilExpiration(contract);
  const progress = contractService.calculateProgress(contract.milestones);
  const canApprove = contractService.canUserApprove(contract, 'currentUserId'); // This would come from auth context
  const canSign = contractService.canUserSign(contract, 'currentUserId');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'milestones', label: `Milestones (${contract.milestones.length})` },
    { id: 'versions', label: `Versions (${contract.versions.length})` },
    { id: 'approvals', label: `Approvals (${contract.approvals.length})` },
    { id: 'comments', label: 'Comments' },
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/projects/${projectId}/contracts`)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{contract.title}</h1>
              <p className="text-sm text-gray-600">{contract.contractNumber}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${statusColor}-100 text-${statusColor}-800`}
            >
              {contractService.getStatusLabel(contract.status)}
            </span>
            {isExpired && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                Expired
              </span>
            )}
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        {(canApprove || canSign) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-900">Action Required</h3>
                <p className="text-sm text-blue-700">
                  {canApprove && 'This contract requires your approval'}
                  {canSign && 'This contract is ready for your signature'}
                </p>
              </div>
              <div className="flex space-x-3">
                {canApprove && (
                  <button
                    onClick={() => dispatch(setShowApprovalModal(true))}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Review & Approve
                  </button>
                )}
                {canSign && (
                  <button
                    onClick={() => dispatch(setShowSignatureModal(true))}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    Sign Contract
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <svg
              className="w-5 h-5 text-red-400 mt-0.5 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => dispatch(clearError())}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-brand-orange text-brand-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'overview' && (
          <div className="p-6">
            {isEditing ? (
              <form onSubmit={handleUpdateContract} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contract Title
                    </label>
                    <input
                      type="text"
                      value={editFormData?.title || ''}
                      onChange={e => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                    <div className="flex">
                      <input
                        type="number"
                        step="0.01"
                        value={editFormData?.amount || ''}
                        onChange={e =>
                          setEditFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) }))
                        }
                        className="block w-full px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                        required
                      />
                      <select
                        value={editFormData?.currency || 'USD'}
                        onChange={e =>
                          setEditFormData(prev => ({ ...prev, currency: e.target.value }))
                        }
                        className="border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <DatePicker
                      label="Start Date"
                      value={
                        editFormData?.startDate
                          ? new Date(editFormData.startDate).toISOString().split('T')[0]
                          : null
                      }
                      onChange={(date) =>
                        setEditFormData(prev => ({ ...prev, startDate: date || '' }))
                      }
                      required
                    />
                  </div>
                  <div>
                    <DatePicker
                      label="End Date"
                      value={
                        editFormData?.endDate
                          ? new Date(editFormData.endDate).toISOString().split('T')[0]
                          : null
                      }
                      onChange={(date) =>
                        setEditFormData(prev => ({ ...prev, endDate: date || '' }))
                      }
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editFormData?.description || ''}
                    onChange={e =>
                      setEditFormData(prev => ({ ...prev, description: e.target.value }))
                    }
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope of Work
                  </label>
                  <textarea
                    value={editFormData?.scope || ''}
                    onChange={e => setEditFormData(prev => ({ ...prev, scope: e.target.value }))}
                    rows={5}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setEditFormData(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-brand-orange-dark disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Contract Value</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {contractService.formatCurrency(contract.amount, contract.currency)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Duration</dt>
                    <dd className="mt-1 text-lg text-gray-900">
                      {contract.duration ? `${contract.duration} days` : 'Not specified'}
                    </dd>
                    {daysUntilExpiration !== null && (
                      <div
                        className={`text-sm mt-1 ${daysUntilExpiration < 0 ? 'text-red-600' : daysUntilExpiration <= 30 ? 'text-yellow-600' : 'text-gray-600'}`}
                      >
                        {daysUntilExpiration < 0
                          ? `${Math.abs(daysUntilExpiration)} days overdue`
                          : daysUntilExpiration === 0
                            ? 'Expires today'
                            : `${daysUntilExpiration} days remaining`}
                      </div>
                    )}
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Progress</dt>
                    <dd className="mt-1">
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                          <div
                            className={`bg-${progress === 100 ? 'green' : 'blue'}-600 h-2 rounded-full`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="text-lg font-medium text-gray-900">{progress}%</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {contract.milestones.filter(m => m.status === 'completed').length} of{' '}
                        {contract.milestones.length} milestones completed
                      </div>
                    </dd>
                  </div>
                </div>

                {/* Description */}
                {contract.description && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Description</dt>
                    <dd className="text-gray-900">{contract.description}</dd>
                  </div>
                )}

                {/* Scope */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-2">Scope of Work</dt>
                  <dd className="text-gray-900 whitespace-pre-wrap">{contract.scope}</dd>
                </div>

                {/* Contract Details */}
                <div className="border-t border-gray-200 pt-6">
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Contract Party</dt>
                      <dd className="mt-1 text-gray-900">
                        <div className="font-medium">{contract.party?.name}</div>
                        <div className="text-sm text-gray-600">{contract.party?.contactPerson}</div>
                        <div className="text-sm text-gray-600">{contract.party?.email}</div>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Contract Dates</dt>
                      <dd className="mt-1 text-gray-900">
                        <div>Start: {new Date(contract.startDate).toLocaleDateString()}</div>
                        <div>End: {new Date(contract.endDate).toLocaleDateString()}</div>
                      </dd>
                    </div>
                    {contract.tags.length > 0 && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Tags</dt>
                        <dd className="mt-1">
                          <div className="flex flex-wrap gap-2">
                            {contract.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="mt-1 text-gray-900">
                        <div>{new Date(contract.createdAt).toLocaleDateString()}</div>
                        <div className="text-sm text-gray-600">by {contract.creator?.name}</div>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'milestones' && <ContractMilestones contract={contract} />}

        {activeTab === 'versions' && <ContractVersions contract={contract} />}

        {activeTab === 'approvals' && <ContractApprovals contract={contract} />}

        {activeTab === 'comments' && <ContractComments contract={contract} />}
      </div>

      {/* Modals */}
      {showSignatureModal && (
        <DigitalSignatureModal
          isOpen={showSignatureModal}
          onClose={() => dispatch(setShowSignatureModal(false))}
          onSign={handleSignature}
          contract={contract}
        />
      )}

      {showApprovalModal && (
        <ApprovalModal
          isOpen={showApprovalModal}
          onClose={() => dispatch(setShowApprovalModal(false))}
          onApprove={handleApproval}
          contract={contract}
        />
      )}
    </div>
  );
};

export default ContractDetails;
